import { FormGroupRules } from '@shared/models/interfaces/form-group-rules.model';
import { FormGroup } from '@angular/forms';
import { addRemoveError, getControl, getGroup } from '@shared/helpers/form.helper';
import { isNilOrEmpty } from '@shared/helpers/utils.helper';
import { toNum } from '@shared/helpers/math.helper';
import { REQUIRED_STATUS } from '@shared/constants/shared.constants';
import { datiBilancioCkHelper, getDatiBilancioControl, getDatiBilancioGroup } from './helpers/dati-bilancio-form.helpers';
import { isEqual } from 'lodash-es';
import { isFutureDay } from '@shared/helpers/date.helper';
import { formPaths } from '@shared/constants/form-paths.const';

const DATA_DELIBERA_NON_FUTURA = 'La data della delibera non può essere una data futura';

export const datiBilancioRistorniRules: FormGroupRules = {
    my_RistorniContoEc: (form: FormGroup) => {
        ristorniContoHelper(form, 'my_SchedaRistorni.my_RistorniContoEc', 'my_CkApplicatiRistorni');
    },

    my_RistorniContoEcAnno1: (form: FormGroup) => {
        ristorniContoHelper(form, 'my_SchedaRistorni.my_RistorniContoEcAnno1', 'my_CkApplicaRistorniAnno1');
    },

    my_lblVerificaDelibere: (form: FormGroup) => {
        const schedaRistorniGroup = getGroup(getDatiBilancioGroup(form), 'my_SchedaRistorni');
        const buildName = (name: string, isFirstYear: boolean) => `${name}${isFirstYear ? 'Anno1' : ''}`;
        const getCkRistorniName = (isFirstYear: boolean) => (isFirstYear ? 'my_CkApplicaRistorniAnno1' : 'my_CkApplicatiRistorni');

        const checkDelibere = (isFirstYear: boolean): boolean => {
            const ckApplicatiRistorni: string = getDatiBilancioControl(form, getCkRistorniName(isFirstYear)).value;
            if (ckApplicatiRistorni !== '1') return false;
            const validaVerifica = toNum(getControl(schedaRistorniGroup, buildName('validaVerificaDelibere', isFirstYear)).value);
            const ristornoCoopLav = toNum(getControl(schedaRistorniGroup, buildName('my_RistornoCoopLav', isFirstYear)).value);
            const ristornoCoopUt = toNum(getControl(schedaRistorniGroup, buildName('my_RistornoCoopUt', isFirstYear)).value);
            const ristornoCoopAp = toNum(getControl(schedaRistorniGroup, buildName('my_RistornoCoopAp', isFirstYear)).value);
            const sum = Math.round((ristornoCoopLav + ristornoCoopUt + ristornoCoopAp) * 100) / 100;
            return validaVerifica !== sum && ckApplicatiRistorni === '1';
        };

        const my_lblVerificaDelibereControl = getControl(schedaRistorniGroup, 'my_lblVerificaDelibere');
        const showError = checkDelibere(false) || checkDelibere(true);
        const errors = my_lblVerificaDelibereControl?.errors ? my_lblVerificaDelibereControl.errors : {};

        addRemoveError(showError, errors, 'my_lblVerificaDelibere', {
            status: 'Attenzione: verificare gli importi inseriti',
            message: 'I valori inseriti alle voci G-H-I devono corrispondere ai ristorni imputati nella sezione "Verifica Importo del Ristorno"',
        });

        my_lblVerificaDelibereControl.setErrors(isEqual(errors, {}) ? null : errors);
    },

    my_lblVerifCont: (form: FormGroup) => {
        const schedaRistorniGroup = getGroup(getDatiBilancioGroup(form), 'my_SchedaRistorni');
        const buildName = (name: string, isFirstYear: boolean) => {
            return `${name}${isFirstYear ? 'Anno1' : ''}`;
        };

        const getCkRistorniName = (isFirstYear: boolean): string => {
            return isFirstYear ? 'my_CkApplicaRistorniAnno1' : 'my_CkApplicatiRistorni';
        };

        const checkRequiredCompilation = (isFirstYear: boolean): boolean => {
            const my_validascheda2: string = getControl(schedaRistorniGroup, buildName('my_validascheda2', isFirstYear)).value;
            const my_CkApplicatiRistorni: string = getDatiBilancioControl(form, getCkRistorniName(isFirstYear)).value;
            return my_validascheda2 === '0' && my_CkApplicatiRistorni === '1';
        };

        const checkSumGTZero = (isFirstYear: boolean): boolean => {
            const my_RistornoCoopLav = toNum(getControl(schedaRistorniGroup, buildName('my_RistornoCoopLav', isFirstYear)).value);
            const my_RistornoCoopUt = toNum(getControl(schedaRistorniGroup, buildName('my_RistornoCoopUt', isFirstYear)).value);
            const my_RistornoCoopAp = toNum(getControl(schedaRistorniGroup, buildName('my_RistornoCoopAp', isFirstYear)).value);
            const my_CkApplicatiRistorni: string = getDatiBilancioControl(form, getCkRistorniName(isFirstYear)).value;
            return my_RistornoCoopLav + my_RistornoCoopUt + my_RistornoCoopAp <= 0 && my_CkApplicatiRistorni === '1';
        };

        const my_lblVerifContControl = getControl(schedaRistorniGroup, 'my_lblVerifCont');
        const showDatiMancantiError = checkRequiredCompilation(true) || checkRequiredCompilation(false);
        const showSumGTZeroError = checkSumGTZero(true) || checkSumGTZero(false);
        const errors = my_lblVerifContControl?.errors ? my_lblVerifContControl.errors : {};

        addRemoveError(showSumGTZeroError, errors, 'my_lblVerifCont', {
            message: 'La somma dei campi D E F deve essere maggiore di 0',
            status: 'Attenzione',
        });

        addRemoveError(showDatiMancantiError, errors, 'my_lblVerifCont', {
            message: 'Deve essere compilata almeno una delle 3 sezioni',
            status: 'Dati mancanti',
        });

        my_lblVerifContControl.setErrors(isEqual(errors, {}) ? null : errors);
    },

    my_DataDelibIntegSal: (form: FormGroup) => {
        const my_DataDelibIntegSal: string = getDatiBilancioControl(form, 'my_SchedaRistorni.my_DataDelibIntegSal')?.value;
        datiBilancioCkHelper(
            form,
            'my_SchedaRistorni.my_DataDelibIntegSal',
            'my_CkApplicatiRistorni',
            1,
            formPaths.bilancio,
            isFutureDay(my_DataDelibIntegSal),
            DATA_DELIBERA_NON_FUTURA
        );
    },

    my_DataDelibAumCapit: (form: FormGroup) => {
        const my_DataDelibAumCapit: string = getDatiBilancioControl(form, 'my_SchedaRistorni.my_DataDelibAumCapit')?.value;
        datiBilancioCkHelper(
            form,
            'my_SchedaRistorni.my_DataDelibAumCapit',
            'my_CkApplicatiRistorni',
            1,
            formPaths.bilancio,
            isFutureDay(my_DataDelibAumCapit),
            DATA_DELIBERA_NON_FUTURA
        );
    },

    my_DataDelibDistribTit: (form: FormGroup) => {
        const my_DataDelibDistribTit: string = getDatiBilancioControl(form, 'my_SchedaRistorni.my_DataDelibDistribTit')?.value;
        datiBilancioCkHelper(
            form,
            'my_SchedaRistorni.my_DataDelibDistribTit',
            'my_CkApplicatiRistorni',
            1,
            formPaths.bilancio,
            isFutureDay(my_DataDelibDistribTit),
            DATA_DELIBERA_NON_FUTURA
        );
    },

    my_DataDelibIntegSalAnno1: (form: FormGroup) => {
        const my_DataDelibIntegSalAnno1: string = getDatiBilancioControl(form, 'my_SchedaRistorni.my_DataDelibIntegSalAnno1')?.value;
        datiBilancioCkHelper(
            form,
            'my_SchedaRistorni.my_DataDelibIntegSalAnno1',
            'my_CkApplicaRistorniAnno1',
            1,
            formPaths.bilancio,
            isFutureDay(my_DataDelibIntegSalAnno1),
            DATA_DELIBERA_NON_FUTURA
        );
    },

    my_DataDelibAumCapitAnno1: (form: FormGroup) => {
        const my_DataDelibAumCapitAnno1: string = getDatiBilancioControl(form, 'my_SchedaRistorni.my_DataDelibAumCapitAnno1')?.value;
        datiBilancioCkHelper(
            form,
            'my_SchedaRistorni.my_DataDelibAumCapitAnno1',
            'my_CkApplicaRistorniAnno1',
            1,
            formPaths.bilancio,
            isFutureDay(my_DataDelibAumCapitAnno1),
            DATA_DELIBERA_NON_FUTURA
        );
    },

    my_DataDelibDistribTitAnno1: (form: FormGroup) => {
        const my_DataDelibDistribTitAnno1: string = getDatiBilancioControl(form, 'my_SchedaRistorni.my_DataDelibDistribTitAnno1')?.value;
        datiBilancioCkHelper(
            form,
            'my_SchedaRistorni.my_DataDelibDistribTitAnno1',
            'my_CkApplicaRistorniAnno1',
            1,
            formPaths.bilancio,
            isFutureDay(my_DataDelibDistribTitAnno1),
            DATA_DELIBERA_NON_FUTURA
        );
    },

    my_AvanzoDiGestionePrevalenza: (form: FormGroup) => {
        datiBilancioCkHelper(form, 'my_SchedaRistorni.my_AvanzoDiGestionePrevalenza', 'my_CkApplicatiRistorni', 1);
    },

    my_AvanzoDiGestionePrevalenzaAnno1: (form: FormGroup) => {
        datiBilancioCkHelper(form, 'my_SchedaRistorni.my_AvanzoDiGestionePrevalenzaAnno1', 'my_CkApplicaRistorniAnno1', 1);
    },

    my_AvanzoDiGestioneRigo23: (form: FormGroup) => {
        datiBilancioCkHelper(form, 'my_SchedaRistorni.my_AvanzoDiGestioneRigo23', 'my_CkApplicatiRistorni', 1);
    },

    my_AvanzoDiGestioneRigo23Anno1: (form: FormGroup) => {
        datiBilancioCkHelper(form, 'my_SchedaRistorni.my_AvanzoDiGestioneRigo23Anno1', 'my_CkApplicaRistorniAnno1', 1);
    },

    my_CkRistorni: (form: FormGroup) => {
        const warningsRistorniGroup = getGroup(form, 'my_WarningsRistorni');
        const my_CkRistorniControl = getDatiBilancioControl(form, 'my_CkRistorni');
        const my_CkRistorni: string = my_CkRistorniControl?.value;
        const my_CkApplicatiRistorni = toNum(getDatiBilancioControl(form, 'my_CkApplicatiRistorni')?.value);
        const my_CkApplicaRistorniAnno1 = toNum(getDatiBilancioControl(form, 'my_CkApplicaRistorniAnno1')?.value);
        const my_CkDSuperioreB = toNum(getControl(warningsRistorniGroup, 'my_CkDSuperioreB')?.value);
        const my_CkDSuperiore30PerCentoC = toNum(getControl(warningsRistorniGroup, 'my_CkDSuperiore30PerCentoC')?.value);
        const my_CkDSuperioreCEB9 = toNum(getControl(warningsRistorniGroup, 'my_CkDSuperioreCEB9')?.value);
        const my_CkESuperioreB = toNum(getControl(warningsRistorniGroup, 'my_CkESuperioreB')?.value);
        const my_CkFSuperioreB = toNum(getControl(warningsRistorniGroup, 'my_CkFSuperioreB')?.value);
        const my_CkGSuperioreB = toNum(getControl(warningsRistorniGroup, 'my_CkGSuperioreB')?.value);
        const my_CkHSuperioreB = toNum(getControl(warningsRistorniGroup, 'my_CkHSuperioreB')?.value);
        const my_CkISuperioreB = toNum(getControl(warningsRistorniGroup, 'my_CkISuperioreB')?.value);
        const my_CkGHISuperioriB = toNum(getControl(warningsRistorniGroup, 'my_CkGHISuperioriB')?.value);
        const errors = my_CkRistorniControl?.errors ? my_CkRistorniControl.errors : {};
        const showRequiredError = isNilOrEmpty(my_CkRistorni) && (my_CkApplicatiRistorni === 1 || my_CkApplicaRistorniAnno1 === 1);
        const somma =
            my_CkDSuperioreB +
            my_CkDSuperiore30PerCentoC +
            my_CkDSuperioreCEB9 +
            my_CkESuperioreB +
            my_CkFSuperioreB +
            my_CkGSuperioreB +
            my_CkHSuperioreB +
            my_CkISuperioreB +
            my_CkGHISuperioriB;
        const showRistorniError = my_CkRistorni === '1' && somma > 0 && (my_CkApplicatiRistorni === 1 || my_CkApplicaRistorniAnno1 === 1);

        addRemoveError(showRequiredError, errors, 'my_CkRistorni', { status: REQUIRED_STATUS });

        addRemoveError(showRistorniError, errors, 'my_CkRistorni', {
            status: 'Attenzione: i ristorni non risultano correttamente ripartiti',
        });

        my_CkRistorniControl?.setErrors(isEqual(errors, {}) ? null : errors);
    },

    my_TotConfSociFin: (form: FormGroup) => {
        const my_TotConfSociFinControl = getDatiBilancioControl(form, 'my_TotConfSociFin');
        const my_TotConfSociFin: string = my_TotConfSociFinControl?.value;
        const my_SociGiuFinData = toNum(getControl(form, `${formPaths.baseSociale}.my_SociGiuFinData`)?.value);
        const my_SociFisFinData = toNum(getControl(form, `${formPaths.baseSociale}.my_SociFisFinData`)?.value);
        const my_CkSociSovventoriTraSociCoop: string = getDatiBilancioControl(form, 'my_CkSociSovventoriTraSociCoop')?.value;
        const errors = my_TotConfSociFinControl?.errors ? my_TotConfSociFinControl.errors : {};
        const showError = isNilOrEmpty(my_TotConfSociFin) && (my_SociGiuFinData > 0 || my_SociFisFinData > 0 || my_CkSociSovventoriTraSociCoop === '1');

        addRemoveError(showError, errors, 'my_TotConfSociFin', { status: REQUIRED_STATUS });

        my_TotConfSociFinControl?.setErrors(isEqual(errors, {}) ? null : errors);
    },

    my_CkObbligoBilSoc: (form: FormGroup) => {
        const my_CkObbligoBilSocControl = getDatiBilancioControl(form, 'my_CkObbligoBilSoc');
        const my_CkObbligoBilSoc: string = my_CkObbligoBilSocControl.value;
        const my_CkImpresaSociale: string = getControl(form, 'my_SezioneI__Rilevazione.my_DatiIdentificativi.my_CkImpresaSociale')?.value;
        const errors = my_CkObbligoBilSocControl?.errors ? my_CkObbligoBilSocControl.errors : {};

        addRemoveError(my_CkObbligoBilSoc != '1' && my_CkImpresaSociale === '1', errors, 'my_CkObbligoBilSoc', {
            status: 'Attenzione: risposta incoerente',
            message: "L'ente è obbligato a redigere il bilancio sociale perchè è impresa sociale",
        });

        my_CkObbligoBilSocControl?.setErrors(isEqual(errors, {}) ? null : errors);
    },

    my_ValoreNomSuddivCapit: (form: FormGroup) => {
        const my_valControl = getDatiBilancioControl(form, 'my_ValoreNomSuddivCapit');
        const errors = my_valControl?.errors ? my_valControl.errors : {};

        addRemoveError(isNilOrEmpty(my_valControl?.value), errors, 'my_ValoreNomSuddivCapit', {
            status: REQUIRED_STATUS,
        });
        my_valControl.setErrors(isEqual(errors, {}) ? null : errors);
    },

    my_CkRegolRedBilConsol: (form: FormGroup) => {
        datiBilancioCkHelper(form, 'my_CkRegolRedBilConsol', 'my_CkRedazBilConsol', 1);
    },

    my_CkRegolDepBilConsol: (form: FormGroup) => {
        datiBilancioCkHelper(form, 'my_CkRegolDepBilConsol', 'my_CkRedazBilConsol', 1);
    },

    my_DescDichFiscPresDaUltRev: (form: FormGroup) => {
        datiBilancioCkHelper(form, 'my_DescDichFiscPresDaUltRev', 'my_CK37DichFiscPresDaUltRev', 0);
    },
};

const ristorniContoHelper = (form: FormGroup, ristorniPath: string, ckPath: string): void => {
    const my_RistorniContoControl = getDatiBilancioControl(form, ristorniPath);
    const ckValue: string = getDatiBilancioControl(form, ckPath)?.value;
    const errors = my_RistorniContoControl?.errors ? my_RistorniContoControl.errors : {};
    const addRequiredError = isNilOrEmpty(my_RistorniContoControl.value) && ckValue === '1';
    const addNoNegativeError = ckValue === '1' && toNum(my_RistorniContoControl.value) < 0;
    addRemoveError(addRequiredError, errors, ristorniPath, { status: REQUIRED_STATUS });
    addRemoveError(addNoNegativeError, errors, ristorniPath, { status: 'Il valore non può essere negativo' });

    my_RistorniContoControl?.setErrors(isEqual(errors, {}) ? null : errors);
};
